import { formatCpmCount } from '../../../constants/centralProductMaster.constants';
import type { CpmMasterDetailsResponse } from '../../../types/centralProductMaster.types';

function asStringList(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.map((v) => String(v ?? '').trim()).filter(Boolean);
}

function trunc(s: string, max = 26): string {
    if (s.length <= max) return s;
    return `${s.slice(0, max - 2)}…`;
}

function nameOf(details: CpmMasterDetailsResponse, fallback: string) {
    return String(details.record?.name ?? fallback);
}

export interface BarcodeOverlapProps {
    sideA: CpmMasterDetailsResponse;
    sideB: CpmMasterDetailsResponse;
    idA: string;
    idB: string;
    survivorId: string | null;
}

export default function BarcodeOverlap({
    sideA,
    sideB,
    idA,
    idB,
    survivorId,
}: BarcodeOverlapProps) {
    const leftKey = survivorId === idB ? 'B' : 'A';
    const rightKey = leftKey === 'A' ? 'B' : 'A';
    const left = leftKey === 'A' ? sideA : sideB;
    const right = rightKey === 'A' ? sideA : sideB;
    const leftId = leftKey === 'A' ? idA : idB;
    const rightId = rightKey === 'A' ? idA : idB;
    const leftName = nameOf(left, leftId);
    const rightName = nameOf(right, rightId);

    const lb = asStringList(left.record?.gtin);
    const rb = asStringList(right.record?.gtin);
    const norm = (x: string) => x.toLowerCase();
    const shared = lb.filter((x) => rb.map(norm).includes(norm(x)));
    const onlyLeft = lb.filter((x) => !rb.map(norm).includes(norm(x)));
    const onlyRight = rb.filter((x) => !lb.map(norm).includes(norm(x)));

    const hasSurvivor = !!survivorId;
    const groups = [
        {
            tone: 'shared' as const,
            label: 'On both records',
            codes: shared,
            emptyLabel: 'No barcode appears on both',
            note: shared.length
                ? 'A shared GTIN is strong evidence these two records are the same pack.'
                : 'These records share no GTIN — confirm the match on volume, pack and brand instead.',
        },
        {
            tone: 'plain' as const,
            label: hasSurvivor ? 'Only on the survivor' : `Only on ${trunc(leftName)}`,
            codes: onlyLeft,
            emptyLabel: 'None unique to this record',
            note: hasSurvivor
                ? `Already on ${trunc(leftName)} — unchanged by the merge.`
                : `Held only by ${trunc(leftName)} today.`,
        },
        {
            tone: 'plain' as const,
            label: hasSurvivor ? 'Only on the retiring record' : `Only on ${trunc(rightName)}`,
            codes: onlyRight,
            emptyLabel: 'None unique to this record',
            note: hasSurvivor
                ? 'These move onto the survivor when you merge.'
                : `Held only by ${trunc(rightName)} today.`,
        },
    ];

    const subhead = shared.length
        ? `${formatCpmCount(shared.length)} barcode${shared.length === 1 ? ' is' : 's are'} on both records — supporting evidence that these are the same pack.`
        : 'No barcode is shared between the two records.';

    return (
        <div
            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col"
            style={{
                borderRadius: 16,
                padding: '20px 22px',
                gap: 14,
                boxShadow: '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
            }}
        >
            <div className="flex items-baseline flex-wrap" style={{ gap: 12 }}>
                <span
                    className="uppercase text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                >
                    Barcode / GTIN overlap
                </span>
                <span
                    className="text-[var(--cpm-text-secondary)] text-pretty"
                    style={{ fontSize: 13, fontWeight: 600 }}
                >
                    {subhead}
                </span>
            </div>
            <div
                className="grid"
                style={{
                    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                    gap: 12,
                    alignItems: 'start',
                }}
            >
                {groups.map((g) => {
                    const shared = g.tone === 'shared';
                    return (
                        <div
                            key={g.label}
                            className="flex flex-col"
                            style={{
                                gap: 9,
                                padding: '14px 15px',
                                borderRadius: 12,
                                border: `1px solid ${shared ? 'var(--cpm-success-border)' : 'var(--cpm-border-default)'}`,
                                background: shared
                                    ? 'var(--cpm-success-bg)'
                                    : 'var(--cpm-surface-subtle)',
                            }}
                        >
                            <div className="flex items-center" style={{ gap: 8 }}>
                                <span
                                    className="inline-flex items-center justify-center shrink-0"
                                    style={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: 7,
                                        background: shared
                                            ? 'var(--cpm-success-text-light)'
                                            : '#fff',
                                        border: shared
                                            ? undefined
                                            : '1px solid var(--cpm-border-default)',
                                        color: shared ? '#fff' : 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    {shared ? (
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.2"
                                        >
                                            <circle cx="9" cy="12" r="6" />
                                            <circle cx="15" cy="12" r="6" />
                                        </svg>
                                    ) : (
                                        <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.2"
                                        >
                                            <path d="M3 5v14M8 5v14M12 5v14M17 5v14M21 5v14" />
                                        </svg>
                                    )}
                                </span>
                                <span
                                    className="uppercase text-[var(--cpm-text-tertiary)] min-w-0 text-pretty"
                                    style={{
                                        fontSize: 11,
                                        fontWeight: 800,
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    {g.label}
                                </span>
                                <span
                                    className="ml-auto inline-flex items-center justify-center tabular"
                                    style={{
                                        minWidth: 22,
                                        height: 20,
                                        padding: '0 6px',
                                        borderRadius: 999,
                                        fontSize: 11,
                                        fontWeight: 800,
                                        background: shared
                                            ? 'var(--cpm-success-text-light)'
                                            : 'var(--cpm-surface-skeleton)',
                                        color: shared ? '#fff' : 'var(--cpm-text-secondary)',
                                    }}
                                >
                                    {formatCpmCount(g.codes.length)}
                                </span>
                            </div>
                            <div className="flex flex-wrap" style={{ gap: 6 }}>
                                {g.codes.length === 0 ? (
                                    <span
                                        className="text-[var(--cpm-text-tertiary)]"
                                        style={{ fontSize: 12, fontWeight: 700 }}
                                    >
                                        {g.emptyLabel}
                                    </span>
                                ) : (
                                    g.codes.map((code) => (
                                        <span
                                            key={code}
                                            className="inline-flex items-center tabular font-mono"
                                            style={{
                                                padding: '4px 9px',
                                                borderRadius: 8,
                                                fontSize: 12,
                                                fontWeight: 800,
                                                border: `1px solid ${shared ? 'var(--cpm-success-border)' : 'var(--cpm-border-default)'}`,
                                                background: shared
                                                    ? 'var(--cpm-success-bg)'
                                                    : 'var(--cpm-surface-subtle)',
                                                color: shared
                                                    ? 'var(--cpm-success-text)'
                                                    : 'var(--cpm-text-primary)',
                                            }}
                                        >
                                            {code}
                                        </span>
                                    ))
                                )}
                            </div>
                            <span
                                className="text-[var(--cpm-text-secondary)] text-pretty"
                                style={{ fontSize: 11.5, fontWeight: 600, lineHeight: 1.45 }}
                            >
                                {g.note}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
